import React, {
    useCallback, useMemo, useState
} from 'react'
import { useHistory, useParams } from 'react-router'
import { useSelector } from 'react-redux'
import styled from 'styled-components'
import {
    EventType, GetEventsQuery, GetEventsQueryVariables,
    GetOrganizationQuery as Query,
    GetOrganizationQueryVariables as QueryVars,
} from 'generated/graphql'
import routes from 'routes'
import { Tabs, NotFound } from 'components'
import FullPageLoading from 'components/FullPageLoading'
import Icon from 'components/Icon'
import { TOP_NAV_HEIGHT } from 'constants/layout'
import { useQuery, useWindowDimensions } from 'hooks'
import { _ } from 'utils'
import { $gray_600 } from 'utils/colors'
import { getNodes } from 'graphql/utils'
import { isOrganizationAdminSelector } from 'features/auth/authSlice'
import { eventPreviewSelector } from 'features/auth/eventPreviewSlice'
import EleventlyLogo from 'assets/Elevently_logo.png'
import { getEvents, getOrganization } from './graphql/query'
import OrganizationTab from './tabs/Organization'
import Users from './tabs/Users'
import Resources from './tabs/Resources'
import Events from './tabs/Events'
import ImageGallery from './tabs/ImageGallery'
import TopNav from './components/TopNav'
import Contact from './tabs/Contact'

type OrganizationValue = {
    organization: Query['organization']
    events: Pick<EventType, 'id' | 'createdAt' | 'startDate' | 'endDate' | 'name' | 'slug'>[]
    refresh: () => Promise<void>
    showTab: (tab: 'organization' | 'users' | 'membership' | 'events' | 'imageGallery') => void
}

export const OrganizationContext = React.createContext<OrganizationValue>({
    organization: undefined,
    events: [],
    refresh: async () => {},
    showTab: () => {},
})

const S = {
    Wrapper: styled.div`
        width: 100vw;
        height: 100vh;
    `,
    PageWrap: styled.div`
        max-height: 100vh;
        overflow: auto;
        background: #F9F9F9;
    `,
    Page: styled.div`
        position: relative;
        max-width: 980px;
        margin: ${TOP_NAV_HEIGHT}px auto 0;
        min-height: calc(100vh - ${TOP_NAV_HEIGHT}px);
        padding: 20px;
    `,
    PageContent: styled.div`
    `,
    PageHeader: styled.div`
        font-weight: bold;
        font-size: 30px;
        line-height: 35px;
        margin-bottom: 20px;
    `,
    GoBackEvent: styled.div`
        display: flex;
        align-items: center;
        margin: 10px 0;
        text-transform: uppercase;
        color: ${$gray_600};
        cursor: pointer;
    `
}

const Organization = () => {
    const history = useHistory()
    const eventPreviewData = useSelector(eventPreviewSelector)
    const [selectedTab, setSelectedTab] = useState<string>()
    const { width: screenWidth } = useWindowDimensions()
    const { organizationId } = useParams<{ organizationId: string }>()
    const isOrganizationAdmin = useSelector(isOrganizationAdminSelector(organizationId))

    const {
        data: organizationNode,
        requestStatus: organizationRequestStatus,
        fetchData: fetchOrganization
    } = useQuery<Query, QueryVars>(getOrganization, {
        id: organizationId
    })
    const {
        data: eventsNode,
        requestStatus: eventRequestStatus,
        fetchData: fetchEvent
    } = useQuery<
        GetEventsQuery, GetEventsQueryVariables
    >(getEvents, { filters: { organization: { id: +organizationId } } })

    const organization = useMemo(() => {
        if (!organizationNode || organizationRequestStatus !== 'succeeded') {
            return
        }
        return organizationNode.organization
    }, [organizationNode, organizationRequestStatus])

    const events = getNodes(eventsNode?.myEvents)

    const refresh = useCallback(async () => {
        await fetchOrganization()
        await fetchEvent()
    }, [fetchEvent, fetchOrganization])

    const organizationContextValue = useMemo(() => ({
        organization,
        events,
        refresh,
        showTab: setSelectedTab
    }), [events, organization, refresh])

    const goBackEvent = useCallback(async () => {
        history.push(routes.generalSettings.getPath({ eventSlug: eventPreviewData?.slug }))
    }, [history, eventPreviewData?.slug])

    if (organizationRequestStatus === 'loading' || organizationRequestStatus === 'idle'
        || eventRequestStatus === 'loading' || eventRequestStatus === 'idle') {
        return <FullPageLoading />
    }

    if (!isOrganizationAdmin) {
        return <NotFound />
    }

    return (
        <OrganizationContext.Provider value={organizationContextValue}>
            <S.Wrapper>
                <TopNav
                    logo={EleventlyLogo}
                    screenWidth={screenWidth}
                    organization={organization}
                />
                <S.PageWrap>
                    <S.Page>
                        {eventPreviewData?.slug && (
                            <S.GoBackEvent onClick={goBackEvent}>
                                <Icon icon="previous" color={$gray_600} size={24} />
                                {_('organization.header.back_to_event')}
                            </S.GoBackEvent>
                        )}
                        <S.PageHeader>{_('organization.header.my_organization')}</S.PageHeader>
                        <Tabs
                            className="pb-0"
                            onSelect={setSelectedTab}
                            defaultActiveKey="organization"
                            activeKey={selectedTab}
                            tabs={[
                                {
                                    id: 'organization',
                                    name: _('organization.tabs.organization'),
                                    Component: OrganizationTab
                                },
                                {
                                    id: 'users',
                                    name: _('organization.tabs.users'),
                                    Component: Users
                                },
                                {
                                    id: 'resources',
                                    name: _('organization.tabs.resources'),
                                    Component: Resources
                                },
                                {
                                    id: 'events',
                                    name: _('organization.tabs.events'),
                                    Component: Events
                                },
                                {
                                    id: 'imageGallery',
                                    name: _('organization.tabs.image_gallery'),
                                    Component: ImageGallery
                                },
                                {
                                    id: 'contact',
                                    name: _('organization.tabs.contact'),
                                    Component: Contact
                                },
                            ]}
                        />
                    </S.Page>
                </S.PageWrap>
            </S.Wrapper>
        </OrganizationContext.Provider>
    )
}

export default Organization
